import React, { useState, useEffect, useMemo, startTransition } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    TablePagination,
    Paper,
    Box,
    Chip,
    CircularProgress,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DeploymentAdminListDetail, DeploymentStatus } from '../../../api/Api';
import clientService from '../../../clientService';
import { useAppState } from 'src/state-management/state-provider';
import { useSnackbar } from 'src/view/components/SnackbarService';
import { formatDate } from 'src/util/date-utils';
import { useNavigate } from 'react-router-dom';
import ConfirmationDialog from 'src/view/components/ConfirmationDialog';

type Order = 'asc' | 'desc';

interface Column {
    id: keyof DeploymentAdminListDetail | 'actions';
    label: string;
    sortable?: boolean;
    filterable?: boolean;
}

const columns: readonly Column[] = [
    { id: 'deployment_name', label: 'Name', sortable: false, filterable: false },
    { id: 'project_name', label: 'Project', sortable: true, filterable: false },
    { id: 'site_name', label: 'Site', sortable: true, filterable: false },
    { id: 'location_name', label: 'Location', sortable: true, filterable: false },
    { id: 'device_name', label: 'Device', sortable: true, filterable: false },
    { id: 'start_date', label: 'Start', sortable: true, filterable: false },
    { id: 'end_date', label: 'End', sortable: true, filterable: false },
    { id: 'status', label: 'Status', sortable: true, filterable: false },
    { id: 'file_count', label: 'Total Files', sortable: true, filterable: false },
    { id: 'inference_count', label: 'Analyzed Files', sortable: true, filterable: false },
    { id: 'actions', label: 'Action', sortable: false, filterable: false },
];

const getStatusChip = (status: DeploymentStatus | null | undefined) => {
    let color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' = 'default';
    switch (status) {
        case DeploymentStatus.Upcoming:
            color = 'warning';
            break;
        case DeploymentStatus.Past:
            color = 'default';
            break;
        case DeploymentStatus.Deployed:
            color = 'info';
            break;
        case DeploymentStatus.Completed:
        case DeploymentStatus.Retrieved:
            color = 'success';
            break;
        case DeploymentStatus.Cancelled:
        case DeploymentStatus.Inaccessible:
            color = 'error';
            break;
        default:
            break;
    }
    return <Chip label={status || '-'} color={color} size="small" />;
};

const Deployments: React.FC = () => {
    const { state, dispatch } = useAppState();
    const { deployments } = state.admin;
    const { showSnackbar, snackbarType } = useSnackbar();

    const [order, setOrder] = useState<Order>('desc');
    const [orderBy, setOrderBy] = useState<keyof DeploymentAdminListDetail>('start_date');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filters, setFilters] = useState<Partial<Record<keyof DeploymentAdminListDetail, string>>>({});
    const [quickFilter, setQuickFilter] = useState<DeploymentStatus | 'All'>('All');
    const [loading, setLoading] = useState(false);

    const [selectedDeployment, setSelectedDeployment] = useState<DeploymentAdminListDetail | null>(null);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const navigate = useNavigate();

    const handleRowClick = (deploymentId: string) => {
        // Navigate to deployment details page if needed in the future
        navigate(`/admin/deployment/${deploymentId}/upload`);
    };

    const handleEditDeployment = (selectedDeployment: DeploymentAdminListDetail) => {
        if (selectedDeployment) {
            startTransition(() => {
                navigate(`/admin/edit-deployment/${selectedDeployment.deployment_id}`);
            });
        }
    };

    const handleDeleteDeployment = async () => {
        if (selectedDeployment) {
            try {
                await clientService.api.deleteApiDeploymentApiV1AdminDeploymentsDeploymentIdDelete(selectedDeployment.deployment_id);
                startTransition(() => {
                    dispatch({ type: 'ADMIN_SET_DEPLOYMENT_LIST', payload: deployments.filter((dep: DeploymentAdminListDetail) => dep.deployment_id !== selectedDeployment.deployment_id) });
                });
            } catch (error) {
                console.error('Failed to delete deployment', error);
            }
        }
        setOpenConfirmDialog(false);
    };

    useEffect(() => {
        const fetchDeployments = async () => {
            setLoading(true);
            try {
                const response = await clientService.api.getAllDeploymentsApiV1AdminDeploymentsGet();
                startTransition(() => {
                    dispatch({ type: 'ADMIN_SET_DEPLOYMENT_LIST', payload: response.data });
                });
            } catch (error) {
                console.error('Failed to fetch deployments:', error);
                showSnackbar({ message: 'Failed to fetch deployments', type: snackbarType.ERROR });
            } finally {
                setLoading(false);
            }
        };

        if (deployments.length === 0) {
            fetchDeployments();
        }
    }, [deployments.length, dispatch, showSnackbar, snackbarType]);

    const handleRequestSort = (property: keyof DeploymentAdminListDetail) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleFilterChange = (property: keyof DeploymentAdminListDetail, value: string) => {
        setFilters({
            ...filters,
            [property]: value,
        });
    };

    const handleQuickFilterChange = (status: DeploymentStatus | 'All') => {
        setQuickFilter(status);
        setPage(0);
    };

    const filteredByQuickFilter = useMemo(() => {
        if (quickFilter === 'All') {
            return deployments;
        }
        return deployments.filter(d => d.status === quickFilter);
    }, [deployments, quickFilter]);

    const filteredByColumn = useMemo(() => {
        return filteredByQuickFilter.filter((deployment) => {
            return Object.entries(filters).every(([key, value]) => {
                const filterKey = key as keyof DeploymentAdminListDetail;
                const deploymentValue = deployment[filterKey];
                if (deploymentValue === null || deploymentValue === undefined) {
                    return value === '';
                }
                return String(deploymentValue).toLowerCase().includes(value.toLowerCase());
            });
        });
    }, [filteredByQuickFilter, filters]);

    const sortedDeployments = useMemo(() => {
        return [...filteredByColumn].sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];

            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;

            if (aValue < bValue) {
                return order === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return order === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [filteredByColumn, order, orderBy]);

    const paginatedDeployments = useMemo(() => {
        return sortedDeployments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [sortedDeployments, page, rowsPerPage]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Paper>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={quickFilter}
                        label="Status"
                        onChange={(e) => handleQuickFilterChange(e.target.value as DeploymentStatus | 'All')}
                    >
                        <MenuItem value="All">All</MenuItem>
                        <MenuItem value={DeploymentStatus.Upcoming}>Upcoming</MenuItem>
                        <MenuItem value={DeploymentStatus.Deployed}>Deployed</MenuItem>
                        <MenuItem value={DeploymentStatus.Past}>Past</MenuItem>
                        <MenuItem value={DeploymentStatus.Completed}>Completed</MenuItem>
                        <MenuItem value={DeploymentStatus.Retrieved}>Retrieved</MenuItem>
                        <MenuItem value={DeploymentStatus.Cancelled}>Cancelled</MenuItem>
                        <MenuItem value={DeploymentStatus.Inaccessible}>Inaccessible</MenuItem>
                    </Select>
                </FormControl>
            </Box>
            <TableContainer>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableCell key={column.id}>
                                        {column.sortable ? (
                                            <TableSortLabel
                                                active={orderBy === column.id}
                                                direction={orderBy === column.id ? order : 'asc'}
                                                onClick={() => handleRequestSort(column.id as keyof DeploymentAdminListDetail)}
                                            >
                                                {column.label}
                                            </TableSortLabel>
                                        ) : (
                                            column.label
                                        )}
                                        {column.filterable && (
                                            <Box sx={{ mt: 1 }}>
                                                <TextField
                                                    size="small"
                                                    variant="outlined"
                                                    placeholder={`Filter ${column.label}`}
                                                    onChange={(e) => handleFilterChange(column.id as keyof DeploymentAdminListDetail, e.target.value)}
                                                    sx={{ minWidth: '120px' }}
                                                />
                                            </Box>
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedDeployments.map((deployment) => (
                                <TableRow
                                    key={deployment.deployment_id}
                                    hover
                                    onClick={() => handleRowClick(deployment.deployment_id)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell>{deployment.deployment_name}</TableCell>
                                    <TableCell>{deployment.project_name}</TableCell>
                                    <TableCell>{deployment.site_name}</TableCell>
                                    <TableCell>{deployment.location_name}</TableCell>
                                    <TableCell>{deployment.device_name}</TableCell>
                                    <TableCell>{formatDate(deployment.start_date)}</TableCell>
                                    <TableCell>{formatDate(deployment.end_date)}</TableCell>
                                    <TableCell>{getStatusChip(deployment.status)}</TableCell>
                                    <TableCell>{deployment.file_count}</TableCell>
                                    <TableCell>{deployment.inference_count}</TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            aria-label="edit"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleEditDeployment(deployment);
                                            }}
                                            color="primary"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            aria-label="delete"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedDeployment(deployment);
                                                setOpenConfirmDialog(true);
                                            }}
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={sortedDeployments.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
            <ConfirmationDialog
                open={openConfirmDialog}
                onCancel={() => setOpenConfirmDialog(false)}
                onConfirm={handleDeleteDeployment}
                title="Delete Deployment"
                content="Are you sure you want to delete this deployment? This action will also delete all associated files and inferences."
            />
        </Paper>
    );
};

export default Deployments;
